/*
 * collection-filter-availability.js
 * ---------------------------------------------------------------------------
 * Hides values of the SIZE filter ("Groesse") that have NO available product in
 * the collection, so a fully sold-out size never appears and can't be selected.
 *
 * SCOPE: only the size filter (the variant-option filter, filter.v.option.*) is
 * touched. Every other filter is left exactly as it is. This script never
 * changes filter logic — it only shows/hides size buttons in the DOM.
 *
 * DATA SOURCE — the collection's OWN rendered grid (NOT products.json):
 *   The theme renders only AVAILABLE products into the grid, each with a
 *   <script class="product-variant-json"> block. So the option values that
 *   appear there (with available:true) are exactly the in-stock sizes, in the
 *   correct market/locale that the customer is browsing. We read the live page
 *   plus the remaining pages of the SAME locale URL, so the whole collection is
 *   covered.
 *
 *   We deliberately do NOT use /collections/<handle>/products.json: it returns
 *   the store's DEFAULT market catalog (ignoring the /en/ etc. the customer is
 *   on), so it reports the wrong availability and hides in-stock sizes.
 *
 * Behaviour rules:
 *   - A size value is hidden ONLY when zero available products have it.
 *   - If at least one available product has that value, it stays visible
 *     (matched on either the value OR the visible label).
 *   - A selected (checked) value is never hidden; a value that turns out
 *     available is un-hidden again.
 *   - Fail-open: if the collection can't be fully read, nothing is hidden.
 */
(function () {
  'use strict';

  // Keep the locale prefix so we read the SAME market the customer is on.
  var match = window.location.pathname.match(/^(.*)\/collections\/([^\/?#]+)/);
  if (!match) return;
  var localePrefix = match[1] || '';
  var handle = match[2];
  var base = localePrefix + '/collections/' + handle;

  var availableValues = new Set();
  var scanComplete = false;

  var CACHE_KEY = 'cfa-sizes:' + base;
  var FRESH_TTL = 10 * 60 * 1000; // skip the refetch if the cache is newer than this

  function norm(value) {
    if (value == null) return '';
    return String(value)
      .toLowerCase()
      .replace(/\s+/g, ' ') // \s includes NBSP in JS -> collapses "3 ml" etc.
      .trim();
  }

  function addVariant(v) {
    if (!v || !v.available) return;
    [v.option1, v.option2, v.option3].forEach(function (opt) {
      if (opt != null && opt !== '') availableValues.add(norm(opt));
    });
  }

  // Read every <script class="product-variant-json"> inside a document/fragment.
  function collectFrom(root) {
    var scripts = root.querySelectorAll('script.product-variant-json');
    scripts.forEach(function (sc) {
      var variants;
      try {
        variants = JSON.parse(sc.textContent);
      } catch (e) {
        return;
      }
      if (Array.isArray(variants)) variants.forEach(addVariant);
    });
  }

  // The collection section's id (the section-data script carries number_of_pages).
  function getSectionId() {
    var els = document.querySelectorAll('script[data-section-data]');
    for (var i = 0; i < els.length; i++) {
      try {
        var d = JSON.parse(els[i].textContent);
        if (d && typeof d.number_of_pages !== 'undefined') {
          return els[i].getAttribute('data-section-id');
        }
      } catch (e) {
        /* keep looking */
      }
    }
    return null;
  }

  function pagesFromDoc(doc) {
    var el = doc.querySelector('script[data-section-data]');
    if (!el) return 1;
    try {
      var d = JSON.parse(el.textContent);
      if (d && d.number_of_pages) return d.number_of_pages;
    } catch (e) {
      /* ignore */
    }
    return 1;
  }

  var sectionId = getSectionId();

  function pageUrl(page, useSection) {
    if (useSection && sectionId) {
      return base + '?section_id=' + encodeURIComponent(sectionId) + '&page=' + page;
    }
    return base + '?page=' + page; // full collection page (always has the data)
  }

  function fetchDoc(page, useSection, isRetry) {
    return fetch(pageUrl(page, useSection), { credentials: 'same-origin' })
      .then(function (r) {
        return r.ok ? r.text() : null;
      })
      .then(function (h) {
        return h ? new DOMParser().parseFromString(h, 'text/html') : null;
      })
      .catch(function () {
        return null;
      })
      .then(function (doc) {
        if (doc || isRetry) return doc;
        // One retry after a short delay to ride out a transient failure.
        return new Promise(function (resolve) {
          setTimeout(resolve, 400);
        }).then(function () {
          return fetchDoc(page, useSection, true);
        });
      });
  }

  function fetchAll() {
    collectFrom(document); // page-1 products already on screen

    var MAX_PAGES = 50;
    var ok = true;
    var useSection = !!sectionId;

    return fetchDoc(1, useSection)
      .then(function (doc) {
        // If the section response had no product data, retry as a full page.
        if (useSection && (!doc || !doc.querySelector('script.product-variant-json'))) {
          useSection = false;
          return fetchDoc(1, false);
        }
        return doc;
      })
      .then(function (doc) {
        if (!doc) {
          ok = false;
          return;
        }
        collectFrom(doc);
        var totalPages = Math.min(pagesFromDoc(doc), MAX_PAGES);

        var promises = [];
        var p;
        for (p = 2; p <= totalPages; p++) {
          promises.push(
            fetchDoc(p, useSection).then(function (d) {
              if (!d) {
                ok = false;
                return;
              }
              collectFrom(d);
            })
          );
        }
        return Promise.all(promises);
      })
      .then(function () {
        scanComplete = ok;
      })
      .catch(function () {
        scanComplete = false;
      });
  }

  function readCache() {
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (!obj || !obj.t || !Array.isArray(obj.v)) return null;
      return obj; // { t: <timestamp>, v: [values] }
    } catch (e) {
      return null;
    }
  }

  function writeCache() {
    try {
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ t: Date.now(), v: Array.from(availableValues) })
      );
    } catch (e) {
      /* ignore quota / disabled-storage errors */
    }
  }

  function useCached(obj) {
    obj.v.forEach(function (x) {
      availableValues.add(x);
    });
    scanComplete = true;
  }

  // ---- Scope: SIZE filter only -------------------------------------------
  function fold(value) {
    var s = String(value == null ? '' : value).toLowerCase();
    if (s.normalize) s = s.normalize('NFKD').replace(/[̀-ͯ]/g, '');
    s = s
      .replace(/ä/g, 'a')
      .replace(/ö/g, 'o')
      .replace(/ü/g, 'u')
      .replace(/ß/g, 'ss');
    return s.trim();
  }
  var SIZE_TOKENS = ['grosse', 'groesse', 'size'];
  function looksLikeSize(text) {
    var f = fold(text);
    for (var i = 0; i < SIZE_TOKENS.length; i++) {
      if (f.indexOf(SIZE_TOKENS[i]) !== -1) return true;
    }
    return false;
  }
  function isSizeGroupInput(input) {
    if (looksLikeSize(input.name)) return true; // e.g. filter.v.option.größe
    var details = input.closest('details');
    if (details) {
      var labelEl = details.querySelector('summary span');
      if (labelEl && looksLikeSize(labelEl.textContent)) return true;
    }
    return false;
  }

  function labelTextFor(input) {
    var labelEl = null;
    if (input.id) {
      var sel = window.CSS && CSS.escape ? CSS.escape(input.id) : input.id;
      try {
        labelEl = document.querySelector('label[for="' + sel + '"]');
      } catch (e) {
        labelEl = null;
      }
    }
    if (!labelEl) labelEl = input.closest('label');
    if (!labelEl) return '';
    var span = labelEl.querySelector('span:not(.count-bubble)');
    return (span ? span.textContent : labelEl.textContent) || '';
  }

  function valueIsAvailable(input) {
    var byValue = norm(input.value);
    var byLabel = norm(labelTextFor(input));
    if (byValue && availableValues.has(byValue)) return true;
    if (byLabel && availableValues.has(byLabel)) return true;
    return false;
  }

  function applyHiding() {
    collectFrom(document); // pick up anything newly rendered

    if (!scanComplete) return; // fail-open until the whole collection is read
    if (availableValues.size === 0) return;

    var inputs = document.querySelectorAll('input[name^="filter.v.option."]');
    inputs.forEach(function (input) {
      if (!isSizeGroupInput(input)) return; // ONLY the size filter

      var container =
        input.closest('.facets__size-item, .facets__swatch-item, .facets__item, li') || input;

      if (input.checked || valueIsAvailable(input)) {
        if (container.hasAttribute('data-availability-hidden')) {
          container.style.display = '';
          container.removeAttribute('data-availability-hidden');
        }
      } else {
        container.style.display = 'none';
        container.setAttribute('data-availability-hidden', '');
      }
    });
  }

  function run() {
    collectFrom(document);

    var cached = readCache();
    // Fresh enough -> use it directly (instant + identical on every load).
    if (cached && Date.now() - cached.t < FRESH_TTL) {
      useCached(cached);
      applyHiding();
      return;
    }

    fetchAll().then(function () {
      if (scanComplete) {
        writeCache(); // store the fresh result as the new known-good
      } else if (cached && cached.v.length) {
        // Fetch failed -> fall back to the last known-good result instead of
        // leaving every size visible, so behaviour stays consistent every time.
        useCached(cached);
      }
      applyHiding();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  var debounceTimer;
  var observer = new MutationObserver(function () {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(applyHiding, 150);
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();
